import { useEffect } from 'react'
import { Controller, useForm, type Resolver } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useClasses } from '@sphere/school/data-access'
import {
  ANNOUNCEMENT_PRIORITY_FORM_OPTIONS,
  ANNOUNCEMENT_TARGET_ROLE_FORM_OPTIONS,
  ANNOUNCEMENT_TARGET_TYPE_FORM_OPTIONS,
  AnnouncementPriority,
  AnnouncementTargetType,
  announcementFormSchema,
  type AnnouncementFormData,
} from '@sphere/communication/domain'
import type { Establishment } from '@sphere/school/domain'
import {
  FormInput,
  FormSelect,
  FormTextarea,
} from '@sphere/shared/ui/design-system'
import styles from './AnnouncementCreateForm.module.scss'

export const ANNOUNCEMENT_CREATE_FORM_ID = 'announcement-create-form'

export interface AnnouncementCreateFormProps {
  defaultEstablishmentId: string
  establishments: Establishment[] | undefined
  isActive: boolean
  onSubmit: (data: AnnouncementFormData) => void
}

export function AnnouncementCreateForm({
  defaultEstablishmentId,
  establishments,
  isActive,
  onSubmit,
}: AnnouncementCreateFormProps) {
  const {
    register,
    handleSubmit,
    control,
    watch,
    reset,
    formState: { errors },
  } = useForm<AnnouncementFormData>({
    resolver: zodResolver(
      announcementFormSchema
    ) as Resolver<AnnouncementFormData>,
    defaultValues: {
      title: '',
      content: '',
      priority: AnnouncementPriority.NORMAL,
      targetType: AnnouncementTargetType.ALL,
      targetRole: '',
      establishmentId: defaultEstablishmentId || '',
      classId: '',
    },
  })

  const targetType = watch('targetType')
  const establishmentIdForClasses = watch('establishmentId')

  const { data: classesPage } = useClasses(
    {
      establishmentId: establishmentIdForClasses || undefined,
      limit: 100,
    },
    {
      enabled:
        targetType === AnnouncementTargetType.CLASS &&
        !!establishmentIdForClasses,
    }
  )
  const classes = classesPage?.data ?? []

  useEffect(() => {
    if (!isActive) {
      return
    }
    reset({
      title: '',
      content: '',
      priority: AnnouncementPriority.NORMAL,
      targetType: AnnouncementTargetType.ALL,
      targetRole: '',
      establishmentId: defaultEstablishmentId || '',
      classId: '',
    })
  }, [isActive, defaultEstablishmentId, reset])

  return (
    <form
      id={ANNOUNCEMENT_CREATE_FORM_ID}
      onSubmit={handleSubmit(onSubmit)}
      noValidate
    >
      <div className={styles.formGrid}>
        <div className={styles.formGroup}>
          <FormInput
            id="announcement-title"
            label="Titre"
            required
            placeholder="Titre de l'annonce"
            {...register('title')}
            error={errors.title?.message}
          />
        </div>

        <div className={styles.formGroup}>
          <Controller
            name="priority"
            control={control}
            render={({ field }) => (
              <FormSelect
                id="announcement-priority"
                name="priority"
                label="Priorité"
                required
                value={field.value}
                onValueChange={field.onChange}
                options={[...ANNOUNCEMENT_PRIORITY_FORM_OPTIONS]}
                placeholder="Sélectionner..."
                error={errors.priority?.message}
              />
            )}
          />
        </div>

        <div className={styles.formGroup}>
          <Controller
            name="targetType"
            control={control}
            render={({ field }) => (
              <FormSelect
                id="announcement-target-type"
                name="targetType"
                label="Ciblage"
                required
                value={field.value}
                onValueChange={field.onChange}
                options={[...ANNOUNCEMENT_TARGET_TYPE_FORM_OPTIONS]}
                placeholder="Sélectionner..."
                error={errors.targetType?.message}
              />
            )}
          />
        </div>

        {targetType === AnnouncementTargetType.ROLE && (
          <div className={styles.formGroup}>
            <Controller
              name="targetRole"
              control={control}
              render={({ field }) => (
                <FormSelect
                  id="announcement-role"
                  name="targetRole"
                  label="Rôle cible"
                  required
                  value={field.value ?? ''}
                  onValueChange={field.onChange}
                  options={[...ANNOUNCEMENT_TARGET_ROLE_FORM_OPTIONS]}
                  placeholder="Choisir un rôle"
                  error={errors.targetRole?.message}
                />
              )}
            />
          </div>
        )}

        {targetType === AnnouncementTargetType.CLASS && (
          <>
            <div className={styles.formGroup}>
              <Controller
                name="establishmentId"
                control={control}
                render={({ field }) => (
                  <FormSelect
                    id="announcement-establishment"
                    name="establishmentId"
                    label="Établissement"
                    required
                    value={field.value ?? ''}
                    onValueChange={field.onChange}
                    options={
                      establishments?.map(est => ({
                        value: est.id,
                        label: est.name,
                      })) ?? []
                    }
                    placeholder="Choisir..."
                    error={errors.establishmentId?.message}
                  />
                )}
              />
            </div>
            <div className={styles.formGroup}>
              <Controller
                name="classId"
                control={control}
                render={({ field }) => (
                  <FormSelect
                    id="announcement-class"
                    name="classId"
                    label="Classe"
                    required
                    disabled={!watch('establishmentId')}
                    value={field.value ?? ''}
                    onValueChange={field.onChange}
                    options={classes.map(cls => ({
                      value: cls.id,
                      label: cls.name,
                    }))}
                    placeholder="Choisir..."
                    error={errors.classId?.message}
                  />
                )}
              />
            </div>
          </>
        )}

        <div className={`${styles.formGroup} ${styles.formGroupFull}`}>
          <FormTextarea
            id="announcement-content"
            label="Contenu"
            required
            placeholder="Message..."
            style={{ minHeight: '150px' }}
            {...register('content')}
            error={errors.content?.message}
          />
        </div>
      </div>
    </form>
  )
}
